import React, { useRef, useState, useEffect, useContext, lazy } from 'react';
import {
  Watermark,
  WidgetHeader,
  ActionIcon,
  WidgetWrapper,
  ReactPDFWrapper,
  ReactDatePickerAgGridStyles,
  ReactDatePicker,
} from '@mummssoftware/common-ui';
import { LicenseManager } from 'ag-grid-enterprise';
import { useTheme } from '@material-ui/core/styles';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { GlobalContext } from '@mummssoftware/global-context';
import { MummsPDFGlobalContext } from '@mummssoftware/mumms-print';
import { FormHelperText, useMediaQuery } from '@material-ui/core';
import {
  parseDateMMDDYYYYSlashes,
  parseDateYYYYMMDD,
} from '@mummssoftware/utils/dates';
import {
  AllMeasuresDefined,
  AllPrintMeasuresDefined,
} from '../MeasuresContext/utils/type';
import { Context } from '../../context';
import MeasuresTable from './Table/MeasuresTable';
import { SubmitESAS } from '../../components/scores';
import { SubmitVitals } from '../../components/vitals';
import {
  MainWrap,
  MeasuresTableWrapper,
  MeasuresTableDiv,
  GoBackDiv,
  SubmitHeader,
} from '../../components/layout/WrapperDiv';
import MeasuresSmallGraph from './Graph/MeasuresSmallGraph';
import MeasuresPrint from '../MeasuresPrint';
import MeasuresFullGraph from './Graph/MeasuresFullGraph';
import { MeasuresContext } from '../MeasuresContext/MeasuresContextProvider';
import { MeasuresMenu } from './MeasuresMenu';
import {
  SHOW_TABLE,
  SET_MEASURE_NAME,
  tableHeightRatio,
  MEASURE_TYPE,
  SET_NEW_MEASURE,
  SET_MEASURE_SELECTED,
  highChartYAxistWidth,
  scoresType,
} from '../MeasuresContext/utils/constants';
import defaults from '../../translations/en.json';

import 'ag-grid-community/dist/styles/ag-grid.css';
import '@mummssoftware/common-ui/style/css/ag-theme-hummingbird.css';

export declare const RouteProps: import('react-router-dom').RouteComponentProps<{
  patientNumber: string;
}>;

export const DateContext = React.createContext({
  updateDate: new Date(),
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setUpdateDate: (date: Date) => {},
});

LicenseManager &&
  LicenseManager.setLicenseKey(process.env.REACT_APP_AG_GRID_KEY as string);

type MeasureProps = {
  patient: mumms.patient;
  currentUser: mumms.User;
  allMeasures: AllMeasuresDefined;
  noWidgetHeader?: boolean;
  jsonMeasures?: any;
  onClick: () => void;
} & typeof RouteProps;

const VitalsContextProvider = lazy(() =>
  import('../VitalsContext/VitalsContextProvider'),
);

export const MeasuresComponent = React.memo(
  ({
    patient,
    onClick,
    currentUser,
    allMeasures,
    noWidgetHeader,
    jsonMeasures,
  }: MeasureProps) => {
    const {
      props: {
        grid: gridTheme = {
          className: '',
        },
      },
    } = useTheme() as any;
    const {
      appTitle,
      smallWidget,
      watermark,
      isPatientActive,
      startLoadingTime,
      onWidgetReady,
      readOnly,
      onRemove,
      doNotShow,
    } = useContext(Context);
    const { jsonMeasures: reactPDFJsonMeasures } = useContext(
      MummsPDFGlobalContext,
    );
    const { appVisit } = useContext(GlobalContext);
    const isNarrowPhoneView = useMediaQuery('(max-width:600px)', {
      noSsr: true,
    });
    /** PIM may send `startDate` as `04/24/2026 09:58:48` (MM/DD/YYYY + time) or `effectivedate` as YYYY-MM-DD. */
    const visitDateSource =
      appVisit?.effectivedate ?? appVisit?.starteventdatetime;
    const visitDate = (() => {
      if (visitDateSource == null || visitDateSource === '') {
        return null;
      }
      const s = String(visitDateSource);
      if (s.includes('/')) {
        return parseDateMMDDYYYYSlashes(s);
      }
      return parseDateYYYYMMDD(s);
    })();

    const { dispatch, showTable, newMeasure } = useContext(MeasuresContext);
    const { allMeasuresTypes } = allMeasures;
    const measureType = localStorage.getItem(MEASURE_TYPE);

    const isScores = measureType === scoresType;

    const measuresTypeNames = Object.keys(allMeasuresTypes);

    const [updateDate, setUpdateDate] = useState(visitDate || new Date());

    const visitDateMs = visitDate?.getTime();
    useEffect(() => {
      if (visitDateMs != null) {
        setUpdateDate(new Date(visitDateMs));
      }
    }, [visitDateSource, visitDateMs]);

    // NOTE this is to get the last column of the table rendered dynamically with correct size
    const targetRef = useRef(null);
    const [dimensions, setDimensions] = useState({
      width: 0,
      height: 0,
    });
    useEffect(() => {
      if (targetRef.current) {
        setDimensions({
          width: (targetRef.current as any).offsetWidth as any,
          height: (targetRef.current as any).offsetHeight as any,
        });
      }
    }, []);

    useEffect(() => {
      const loadingDuration = (Date.now() - startLoadingTime) / 1000;
      onWidgetReady && onWidgetReady('Measures' as mumms.ComponentName);
      console.log('## MEASURES WIDGET END ##', loadingDuration, 's');

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const goBack = () => {
      // reset all
      dispatch({
        type: SHOW_TABLE,
        payload: false,
      });
      dispatch({
        type: SET_MEASURE_NAME,
        payload: null,
      });
      dispatch({
        type: SET_NEW_MEASURE,
        payload: false,
      });
      dispatch({
        type: SET_MEASURE_SELECTED,
        payload: null,
      });
    };

    const displayTableHeight = dimensions.height * tableHeightRatio;
    // min height as we need to be able to see 1 row display and the dropdown of 95 px
    const minDisplayTableHeight = 150;
    const tableHeight = Math.max(displayTableHeight, minDisplayTableHeight);
    // todo adjust this
    const tableFullChartWidth = 0.97;

    const displayTableWidth =
      dimensions.width * tableFullChartWidth - highChartYAxistWidth;
    const showSubmitBar = !smallWidget && !readOnly;

    /** Expanded widget on a narrow viewport: free header space for menu + date + submit. */
    const widgetHeaderTitle =
      isNarrowPhoneView && !smallWidget
        ? ''
        : appTitle?.toLocaleUpperCase() || defaults.measures;

    return (
      <ReactDatePickerAgGridStyles>
        <DateContext.Provider value={{ updateDate, setUpdateDate }}>
          <VitalsContextProvider>
            <MainWrap id="mainwrap">
              {!noWidgetHeader && (
                <WidgetHeader
                  appTitle={widgetHeaderTitle}
                  versionInfo={`Measures v${process.env.REACT_APP_MEASURES_VERSION}, SmartChart v${process.env.REACT_APP_VERSION}`}
                  smallWidget={smallWidget}
                  onClick={onClick}
                  onRemove={onRemove}
                  doNotShow={doNotShow}
                >
                  <MeasuresMenu />
                  {showSubmitBar && (
                    <SubmitHeader id="widget-header-submit">
                      {!isNarrowPhoneView && (
                        <FormHelperText
                          id="component-helper-text"
                          style={{
                            marginLeft: 'clamp(4px, 1vw, 12px)',
                            fontSize: 'clamp(0.8125rem, 2vh, 1rem)',
                            fontWeight: 'bold',
                          }}
                        >
                          {appVisit ? defaults.visitDate : defaults.pickDate}
                        </FormHelperText>
                      )}
                      <div
                        style={{
                          marginLeft: isNarrowPhoneView
                            ? 0
                            : 'clamp(4px, 0.5vw, 10px)',
                        }}
                      >
                        <ReactDatePicker
                          id="measures-date-picker"
                          popperPosition="fixed"
                          selectedDate={updateDate}
                          onChange={(date: Date) =>
                            date
                              ? setUpdateDate(date)
                              : setUpdateDate(new Date())
                          }
                          disabled={!!visitDate}
                          maximumDate={new Date()}
                          inputWidth="clamp(108px, 8vw, 180px)"
                        />
                      </div>
                      {isScores ? <SubmitESAS /> : <SubmitVitals />}
                    </SubmitHeader>
                  )}
                </WidgetHeader>
              )}

              <WidgetWrapper
                id="measures-body"
                ref={targetRef}
                smallWidget={smallWidget}
              >
                {watermark && watermark.show ? (
                  <Watermark
                    readOnly={watermark.readOnly}
                    smallWidget={smallWidget}
                  />
                ) : null}
                {showTable && (
                  <GoBackDiv id="goback">
                    <ActionIcon
                      title={defaults.goBack}
                      style={{ margin: 0 }}
                      Icon={ArrowBackIcon}
                      onClick={goBack}
                    />
                  </GoBackDiv>
                )}
                {smallWidget && (
                  <>
                    <MeasuresSmallGraph
                      episodes={patient.episodes}
                      allMeasures={allMeasures}
                      smallWidget={smallWidget}
                      height={dimensions.height}
                      siteTimezone={currentUser.siteTimezone}
                    />
                    {reactPDFJsonMeasures && (
                      <ReactPDFWrapper reactPdfView>
                        <MeasuresPrint
                          allMeasures={
                            reactPDFJsonMeasures as AllPrintMeasuresDefined
                          }
                        />
                      </ReactPDFWrapper>
                    )}
                  </>
                )}

                {!smallWidget && !newMeasure && (
                  <MeasuresFullGraph
                    episodes={patient.episodes}
                    allMeasures={allMeasures}
                    smallWidget={smallWidget}
                    height={dimensions.height}
                    siteTimezone={currentUser.siteTimezone}
                  />
                )}
                {showTable && (
                  <MeasuresTableDiv id="table-display">
                    <MeasuresTableWrapper
                      width={displayTableWidth}
                      height={tableHeight}
                      className={`${gridTheme.className} light`}
                    >
                      <MeasuresTable
                        patient={patient}
                        currentUser={currentUser}
                        allMeasures={allMeasures}
                        width={displayTableWidth}
                        isPatientActive={isPatientActive}
                      />
                    </MeasuresTableWrapper>
                  </MeasuresTableDiv>
                )}
              </WidgetWrapper>
            </MainWrap>
          </VitalsContextProvider>
        </DateContext.Provider>
      </ReactDatePickerAgGridStyles>
    );
  },
);

export default MeasuresComponent;
